// libraries
import React, { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import 'dayjs/locale/nb'

// components
import Section from '/react4xp/components/Section'
import Link from '/react4xp/components/Link'
import Image from '/react4xp/components/Image'
import RichText from '/react4xp/components/RichText'
import MasonryGrid from '/react4xp/components/MasonryGrid'
import Popup from '/react4xp/components/Popup'
import Form from '/react4xp/components/Form/Form'
import TextArea from '/react4xp/components/Form/TextArea'
import Input from '/react4xp/components/Form/Input'
import Button from '/react4xp/components/Button'

// svg
import { UxArrowDown } from '/react4xp/components/Svg/Ux/Arrow'
import { GeneralChat, GeneralHeart, GeneralLink, GeneralTrash } from '/react4xp/components/Svg/Icons/General'

// utils
import { handleAnonymousContentLiked } from '/lib/reactutils/utils'

// prototype comments
//import { comments } from './prototype-comments'

// prop types
export interface IdeaProps {
    services: {
        likeIdea: string
        deleteIdea: string
        reportIdea: string
        commentIdea: string
    }
    ideaId: string
    image: {
        link: string
        alt: string
    }
    title: string
    extraField?: string
    description: string
    totalComments: number
    totalLikes: number
    authorName: string
    date: string
    isLiked: boolean
    isAuthor: boolean
    isLogged: boolean
    pagination: {
        page: number
        pageSize: number
        endpoint: string
    }
    strings: {
        deleteIdeaButton: string
        deleteIdeaConfirmationClose: string
        deleteIdeaConfirmationTitle: string
        deleteIdeaConfirmationContent: string
        deleteIdeaErrorClose: string
        deleteIdeaErrorTitle: string
        deleteIdeaErrorContent: string
        copyLink: string
        reportIdea: string
        reportIdeaConfirmationClose: string
        reportIdeaConfirmationTitle: string
        reportIdeaConfirmationContent: string
        reportIdeaConfirmationCancel: string
        reportIdeaConfirmationConfirm: string
        reportIdeaErrorClose: string
        reportIdeaErrorTitle: string
        reportIdeaErrorContent: string
        reportIdeaSuccessClose: string
        reportIdeaSuccessTitle: string
        reportIdeaSuccessContent: string
        commentBalloon: string
        commentIdeaErrorClose: string
        commentIdeaErrorTitle: string
        commentIdeaErrorContent: string
        commentIdeaInputLabel: string
        commentIdeaInputMaxLength: string
        commentIdeaInputValidationRequired: string
        commentIdeaInputValidationMaxLength: string
        commentIdeaInputValidationMinLength: string
        commentIdeaSubmit: string
        loginButton: string
        reportComment: string
        reportCommentConfirmationClose: string
        reportCommentConfirmationTitle: string
        reportCommentConfirmationContent: string
        reportCommentConfirmationCancel: string
        reportCommentConfirmationConfirm: string
        reportCommentErrorClose: string
        reportCommentErrorTitle: string
        reportCommentErrorContent: string
        reportCommentSuccessClose: string
        reportCommentSuccessTitle: string
        reportCommentSuccessContent: string
        commentIdeaLoadMore: string
        linkCopied: string
        mustBeLoggedIn: string
        deleteIdeaConfirmationCancel: string
        deleteIdeaConfirmationConfirm: string
    }
    profilePageUrl: string
    userPageUrl: string
}

export default function Idea({
    services,
    ideaId,
    image,
    title,
    extraField,
    description,
    totalComments,
    totalLikes,
    authorName,
    date,
    isLiked,
    isAuthor,
    isLogged,
    pagination,
    strings,
    profilePageUrl,
    userPageUrl
}: IdeaProps) {
    
    // general variables
    const [commentsNumber, setComentsNumber] = useState(totalComments)
    const [likesNumber, setLikesNumber] = useState(totalLikes)

    // Like Q Idea
    const [liked, setLiked] = useState(isLiked || false)
    const toggleLike = async (event) => {
        event.target.disabled = true
        try {
            const response = await fetch(services.likeIdea, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    isLiked: !liked
                })
            })

            const data = await response.json()

            if (response.ok) {
                setLikesNumber(data.totalLikes)
                setLiked(data.isLiked)

                if (!isLogged) {
                    handleAnonymousContentLiked(ideaId, data.isLiked)
                }
            }
        } catch (error) {
            console.log('error', error)
        }
        event.target.disabled = false
    }
    useEffect(() => {
        try {
            if (!isLogged) {
                const anonymousContentLiked = localStorage.getItem('anonymous-content-liked')
                if (anonymousContentLiked) {
                    const anonymousContentLikedData = JSON.parse(anonymousContentLiked)
                    if (anonymousContentLikedData.includes(ideaId)) {
                        setLiked(true)
                    } else {
                        setLiked(false)
                    }
                } else {
                    localStorage.setItem('anonymous-content-liked', JSON.stringify([]))
                    setLiked(false)
                }
            }
        } catch(err) {
            console.log('error', err)
        }
    }, [])

    // Delete Q Idea
    const popupDeleteIdeaConfirmation = useRef<HTMLAnchorElement>(null)
    const popupDeleteIdeaError = useRef<HTMLAnchorElement>(null)

    const [renderDeleteIdeaConfirmation, setRenderDeleteIdeaConfirmation] = useState(false)
    const [renderDeleteIdeaError, setRenderDeleteIdeaError] = useState(false)

    const closeDeleteIdeaModal = () => { setRenderDeleteIdeaConfirmation(false) }
    const closeDeleteIdeaErrorModal = () => { setRenderDeleteIdeaError(false) }

    const openDeleteIdeaModal = () => {
        setRenderDeleteIdeaConfirmation(true)
        setTimeout(() => {
            popupDeleteIdeaConfirmation?.current?.click()
        }, 100)
    }

    const openDeleteIdeaErrorModal = () => {
        setRenderDeleteIdeaError(true)
        setTimeout(() => {
            popupDeleteIdeaError?.current?.click()
        }, 100)
    }

    const handleDeleteIdea = async () => {
        try {
            const response = await fetch(services.deleteIdea, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.ok) {
                window.location.href = userPageUrl
            } else {
                setTimeout(() => {
                    closeDeleteIdeaModal()
                    openDeleteIdeaErrorModal()
                }, 500)
            }
        } catch (error) {
            setTimeout(() => {
                console.log('error', error)
                closeDeleteIdeaModal()
                openDeleteIdeaErrorModal()
            }, 500)
        }
    }

    // Report Q Idea
    const popupReportConfirmation = useRef<HTMLAnchorElement>(null)
    const popupReportError = useRef<HTMLAnchorElement>(null)
    const popupReportSuccess = useRef<HTMLAnchorElement>(null)

    const [renderReportConfirmation, setRenderReportConfirmation] = useState(false)
    const [renderReportError, setRenderReportError] = useState(false)
    const [renderReportSuccess, setRenderReportSuccess] = useState(false)

    const closeReportModal = () => { setRenderReportConfirmation(false) }
    const closeReportErrorModal = () => { setRenderReportError(false) }
    const closeReportSuccessModal = () => { setRenderReportSuccess(false) }

    const openReportModal = () => {
        setRenderReportConfirmation(true)
        setTimeout(() => {
            popupReportConfirmation?.current?.click()
        }, 100)
    }

    const openReportErrorModal = () => {
        setRenderReportError(true)
        setTimeout(() => {
            popupReportError?.current?.click()
        }, 100)
    }

    const openReportSuccessModal = () => {
        setRenderReportSuccess(true)
        setTimeout(() => {
            popupReportSuccess?.current?.click()
        }, 100)
    }

    const handleReport = async () => {
        try {
            const response = await fetch(services.reportIdea, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.ok) {
                setTimeout(() => {
                    console.log('success')
                    closeReportModal()
                    openReportSuccessModal()
                }, 500)
            } else {
                setTimeout(() => {
                    closeReportModal()
                    closeReportSuccessModal()
                    openReportErrorModal()
                }, 500)
            }
        } catch (error) {
            setTimeout(() => {
                console.log('error', error)
                closeReportModal()
                closeReportSuccessModal()
                openReportErrorModal()
            }, 500)
        }
    }

    // Report Idea comment
    const popupReportCommentConfirmation = useRef<HTMLAnchorElement>(null)
    const popupReportCommentError = useRef<HTMLAnchorElement>(null)
    const popupReportCommentSuccess = useRef<HTMLAnchorElement>(null)

    const [renderReportCommentConfirmation, setRenderReportCommentConfirmation] = useState(false)
    const [renderReportCommentError, setRenderReportCommentError] = useState(false)
    const [renderReportCommentSuccess, setRenderReportCommentSuccess] = useState(false)
    const [reportCommentServiceUrl, setReportCommentServiceUrl] = useState('')

    const closeReportCommentModal = () => { setRenderReportCommentConfirmation(false) }
    const closeReportCommentErrorModal = () => { setRenderReportCommentError(false) }
    const closeReportCommentSuccessModal = () => { setRenderReportCommentSuccess(false) }

    const openReportCommentModal = (reportCommentServiceUrlParam) => {
        console.log(reportCommentServiceUrlParam)
        setReportCommentServiceUrl(reportCommentServiceUrlParam)
        setRenderReportCommentConfirmation(true)
        setTimeout(() => {
            popupReportCommentConfirmation?.current?.click()
        }, 100)
    }

    const openReportCommentErrorModal = () => {
        setRenderReportCommentError(true)
        setTimeout(() => {
            popupReportCommentError?.current?.click()
        }, 100)
    }

    const openReportCommentSuccessModal = () => {
        setRenderReportCommentSuccess(true)
        setTimeout(() => {
            popupReportCommentSuccess?.current?.click()
        }, 100)
    }

    const handleReportComment = async () => {
        console.log(reportCommentServiceUrl)
        try {
            const response = await fetch(reportCommentServiceUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            const data = await response.json()

            if (response.ok) {
                setTimeout(() => {
                    console.log('success')
                    closeReportCommentModal()
                    openReportCommentSuccessModal()
                }, 500)
            } else {
                setTimeout(() => {
                    console.log('error', data)
                    closeReportCommentSuccessModal()
                    closeReportCommentModal()
                    openReportCommentErrorModal()
                }, 500)
            }
        } catch (error) {
            setTimeout(() => {
                console.log('error', error)
                closeReportCommentSuccessModal()
                closeReportCommentModal()
                openReportCommentErrorModal()
            }, 500)
        }
    }

    // Fetch comments
    const [reviews, setReviews] = useState<any[]>([])
    const [page, setPage] = useState(pagination.page || 1)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [loadMore, setLoadMore] = useState(false)

    useEffect(() => {
        handleFetchComments()
    }, [pagination.pageSize, page])

    const handleFetchComments = async () => {

        if (!pagination.endpoint) return;

        const url = new URL(pagination.endpoint);
        const queryParams = new URLSearchParams();

        if (page) queryParams.append('page', String(page));
        if (pagination.pageSize) queryParams.append('pageSize', pagination.pageSize.toString());
        queryParams.append('contentId', ideaId);

        url.search = queryParams.toString();

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }
            const data = await response.json();

            if (loadMore) {
                setReviews([...reviews, ...data.hits]);
                setLoadMore(false)
            } else {
                setReviews(data.hits);
            }

            setHasNextPage(data.hasNextPage);

        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
        }
    }

    const handleLoadMoreReviews = () => {
        if (hasNextPage) {
            setLoadMore(true)
            setPage(page + 1)
        }
    }

    // copy url
	const [showTooltip, setShowTooltip] = useState(false)
	
	const copyToClipboard = () => {
		navigator.clipboard.writeText(location.href)
		setShowTooltip(true)
    	setTimeout(() => setShowTooltip(false), 3000)
	}

    return (
        <>
            <Section
                bgColor='white'
                paddingTop='small'
                paddingBottom='big'
                className='idea-internal'
            >
                <div className='container'>
                    <div className='row'>

                        <div className='col-md-6 col-md-push-6 right'>
                            {image?.link && (
                                <div className='image'>
                                    <Image
                                        src={image?.link}
                                        srcMedium={image?.link}
                                        width={700}
                                        alt={image?.alt}
                                        className='cover'
                                    />
                                </div>
                            )}
                        </div>

                        <div className='col-md-6 col-md-pull-6 left'>

                            <h1 className='h1 title'>
                                {title}
                            </h1>

                            {extraField && (
                                <p className='extra-field'>
                                    <i>{extraField}</i>
                                </p>
                            )}

                            <div className='interactive-tags'>

                                <button
                                    className='likes'
                                    onClick={(e) => toggleLike(e)}
                                >

                                    <span className={clsx('circle', liked && 'is-liked')}>
                                        <GeneralHeart variant='outline' />
                                    </span>

                                    <span className='text-small'>
                                        {likesNumber}
                                    </span>

                                </button>

                                <button className='comments' onClick={() => {
                                    document.getElementById('user-review-comments')?.scrollIntoView({ behavior: 'smooth' })
                                }}>

                                    <span className='circle'>
                                        <GeneralChat variant='outline' />
                                    </span>

                                    <span className='text-small'>
                                        {commentsNumber}
                                    </span>

                                </button>

                            </div>

                            <p className='h4 person-name'>
                                {authorName}
                            </p>

                            {date &&
                                <p className='text-smaller when'>
                                    {dayjs(date).locale('nb').format('D. MMM YYYY')}
                                </p>
                            }

                            {isAuthor && (
                                <div className='buttons'>
                                    <Button
                                        className='text-small delete'
                                        hasBorders
                                        smallBorders
                                        noUnderline
                                        onClick={openDeleteIdeaModal}
                                    >
                                        {strings.deleteIdeaButton}
                                        <GeneralTrash variant='outline' />
                                    </Button>

                                    {renderDeleteIdeaConfirmation &&
                                        <Popup
                                            size='medium'
                                            ref={popupDeleteIdeaConfirmation}
                                            hasCloseButton
                                            closeButtonText={strings.deleteIdeaConfirmationClose}
                                            title={strings.deleteIdeaConfirmationTitle}
                                            content={strings.deleteIdeaConfirmationContent}
                                            className='popup--exclude-popup'
                                            actionButtons={{
                                                primary: {
                                                    text: strings.deleteIdeaConfirmationCancel,
                                                    closeOnClick: true
                                                },
                                                secondary: {
                                                    text: strings.deleteIdeaConfirmationConfirm,
                                                    onClick: () => handleDeleteIdea(),
                                                    type: 'submit',
                                                    closeOnClick: false
                                                }
                                            }}
                                            onClose={closeDeleteIdeaModal}
                                        />
                                    }

                                    {renderDeleteIdeaError &&
                                        <Popup
                                            size='medium'
                                            ref={popupDeleteIdeaError}
                                            hasCloseButton
                                            closeButtonText={strings.deleteIdeaErrorClose}
                                            title={strings.deleteIdeaErrorTitle}
                                            content={strings.deleteIdeaErrorContent}
                                            className='popup--exclude-popup'
                                            actionButtons={{
                                                primary: {
                                                    text: strings.deleteIdeaErrorClose,
                                                    closeOnClick: true
                                                }
                                            }}
                                            onClose={closeDeleteIdeaErrorModal}
                                        />
                                    }

                                </div>
                            )}

                            {description &&
                                <div className='desc'>
                                    <RichText
                                        content={description}
                                    />
                                </div>
                            }

                            <div className='link'>
                                <Link
                                    text={strings.copyLink}
                                    iconPosition='left'
                                    icon={<GeneralLink variant='outline' />}
                                    data-tooltip={strings.linkCopied}
                                    onClick={copyToClipboard}
                                    className={showTooltip ? 'tooltip-visible' : ''}
                                />
                            </div>

                            {!isAuthor && (
                                <div className='report'>
                                    <button className='text-smaller' onClick={openReportModal}>
                                        <u>
                                            {strings.reportIdea}
                                        </u>
                                    </button>

                                    {renderReportConfirmation &&
                                        <Popup
                                            size='medium'
                                            ref={popupReportConfirmation}
                                            hasCloseButton
                                            closeButtonText={strings.reportIdeaConfirmationClose}
                                            title={strings.reportIdeaConfirmationTitle}
                                            content={strings.reportIdeaConfirmationContent}
                                            className='popup--exclude-popup'
                                            actionButtons={{
                                                primary: {
                                                    text: strings.reportIdeaConfirmationCancel,
                                                    closeOnClick: true
                                                },
                                                secondary: {
                                                    text: strings.reportIdeaConfirmationConfirm,
                                                    onClick: () => handleReport(),
                                                    type: 'submit',
                                                    closeOnClick: false
                                                }
                                            }}
                                            onClose={closeReportModal}
                                        />
                                    }

                                    {renderReportError &&
                                        <Popup
                                            size='medium'
                                            ref={popupReportError}
                                            hasCloseButton
                                            closeButtonText={strings.reportIdeaErrorClose}
                                            title={strings.reportIdeaErrorTitle}
                                            content={strings.reportIdeaErrorContent}
                                            className='popup--exclude-popup'
                                            actionButtons={{
                                                primary: {
                                                    text: strings.reportIdeaErrorClose,
                                                    closeOnClick: true
                                                }
                                            }}
                                            onClose={closeReportErrorModal}
                                        />
                                    }

                                    {renderReportSuccess &&
                                        <Popup
                                            size='medium'
                                            ref={popupReportSuccess}
                                            hasCloseButton
                                            closeButtonText={strings.reportIdeaSuccessClose}
                                            title={strings.reportIdeaSuccessTitle}
                                            content={strings.reportIdeaSuccessContent}
                                            className='popup--exclude-popup'
                                            actionButtons={{
                                                primary: {
                                                    text: strings.reportIdeaSuccessClose,
                                                    closeOnClick: true
                                                }
                                            }}
                                            onClose={closeReportSuccessModal}
                                        />
                                    }

                                </div>
                            )}

                        </div>

                    </div>
                </div>
            </Section>

            <Section bgColor='white-dark' className='idea-internal__comments'>
                <div className='container' id='user-review-comments'>

                    <div className='title-top'>
                        <h2 className='h3'>
                            {strings.commentBalloon}
                        </h2>
                    </div>

                    {isLogged ? (
                        <div className='row'>
                            <div className='col-sm-10 offset-sm-1 col-md-8 offset-md-2 col-lg-6 offset-lg-3 col-xl-4 offset-xl-4'>
                                <Form
                                    endpoint={services.commentIdea}
                                    className={clsx('post-comment-form')}
                                    onSuccess={() => {
                                        window.location.reload()
                                    }}
                                    popupErrorContent={{
                                        hasCloseButton: true,
                                        closeButtonText: strings.commentIdeaErrorClose,
                                        title: strings.commentIdeaErrorTitle,
                                        content: strings.commentIdeaErrorContent,
                                        actionButtons: {
                                            primary: {
                                                text: strings.commentIdeaErrorClose,
                                                closeOnClick: true,
                                                //onClick: () => { alert('test') }
                                            }
                                        }
                                    }}
                                >

                                    <TextArea
                                        id='review-content-message'
                                        label={strings.commentIdeaInputLabel}
                                        name='review-content-message'
                                        required
                                        size='full-width'
                                        minLength={5}
                                        maxLength={{
                                            limit: 200,
                                            text: strings.commentIdeaInputMaxLength
                                        }}
                                        errorMessages={{
                                            required: strings.commentIdeaInputValidationRequired,
                                            maxLength: strings.commentIdeaInputValidationMaxLength,
                                            minLength: strings.commentIdeaInputValidationMinLength
                                        }}
                                    />

                                    <Input
                                        id='review-content-id'
                                        type='hidden'
                                        name='review-content-id'
                                        value={ideaId}
                                    />

                                    <Input
                                        id='review-date'
                                        type='hidden'
                                        name='review-date'
                                        value={dayjs().format('YYYY-MM-DDTHH:mm:ss')}
                                    />

                                    <Button
                                        type='submit'
                                        hover='blue'
                                        hasBorders
                                        noUnderline
                                    >
                                        {strings.commentIdeaSubmit}
                                    </Button>

                                </Form>
                            </div>
                        </div>
                    ) : (
                        <Button
                            href={profilePageUrl}
                            hasBorders
                            hover='blue'
                            noUnderline
                            className='button--login'
                        >
                            {strings.loginButton}
                        </Button>
                    )}

                    <div className='all-comments'>
                        <div className='all-comments__grid'>

                            <MasonryGrid
                                items={reviews}
                                //items={comments}
                                columnGutter={20}
                                maxColumnCount={2}
                                breakpointCols={{
                                    default: 2,
                                    676: 1
                                }}
                                columnWidth={333}
                                render={({ data }, index) => (
                                    <div className='block bg-white' key={index}>

                                        <p className='text-big name'>
                                            <b>
                                                {data.name}
                                            </b>
                                        </p>

                                        <p className='text-smaller when'>
                                            {dayjs(data.date).locale('nb').format('D. MMM YYYY')}
                                        </p>

                                        <RichText
                                            className='comment text-small'
                                            content={data.content}
                                        />

                                        <Link
                                            className='report text-smaller'
                                            onClick={() => openReportCommentModal(data.reportServiceUrl)}
                                            text={strings.reportComment}
                                        />

                                        {renderReportCommentConfirmation &&
                                            <Popup
                                                size='medium'
                                                ref={popupReportCommentConfirmation}
                                                hasCloseButton
                                                closeButtonText={strings.reportCommentConfirmationClose}
                                                title={strings.reportCommentConfirmationTitle}
                                                content={strings.reportCommentConfirmationContent}
                                                className='popup--exclude-popup'
                                                actionButtons={{
                                                    primary: {
                                                        text: strings.reportCommentConfirmationCancel,
                                                        closeOnClick: true
                                                    },
                                                    secondary: {
                                                        text: strings.reportCommentConfirmationConfirm,
                                                        onClick: () => handleReportComment(),
                                                        type: 'submit',
                                                        closeOnClick: false
                                                    }
                                                }}
                                                onClose={closeReportModal}
                                            />
                                        }

                                        {renderReportCommentError &&
                                            <Popup
                                                size='medium'
                                                ref={popupReportCommentError}
                                                hasCloseButton
                                                closeButtonText={strings.reportCommentErrorClose}
                                                title={strings.reportCommentErrorTitle}
                                                content={strings.reportCommentErrorContent}
                                                className='popup--exclude-popup'
                                                actionButtons={{
                                                    primary: {
                                                        text: strings.reportCommentErrorClose,
                                                        closeOnClick: true
                                                    }
                                                }}
                                                onClose={closeReportCommentErrorModal}
                                            />
                                        }

                                        {renderReportCommentSuccess &&
                                            <Popup
                                                size='medium'
                                                ref={popupReportCommentSuccess}
                                                hasCloseButton
                                                closeButtonText={strings.reportCommentSuccessClose}
                                                title={strings.reportCommentSuccessTitle}
                                                content={strings.reportCommentSuccessContent}
                                                className='popup--exclude-popup'
                                                actionButtons={{
                                                    primary: {
                                                        text: strings.reportCommentSuccessClose,
                                                        closeOnClick: true
                                                    }
                                                }}
                                                onClose={closeReportCommentSuccessModal}
                                            />
                                        }

                                    </div>
                                )}
                            />

                            {hasNextPage && (
                                <div className='load-more'>
                                    <Link
                                        text={strings.commentIdeaLoadMore}
                                        onClick={handleLoadMoreReviews}
                                        iconPosition='right'
                                        icon={<UxArrowDown />}
                                    />
                                </div>
                            )}

                        </div>
                    </div>

                </div>
            </Section>
        </>
    )
}
